import { useState, useEffect, useMemo } from "react";
import { ArrowRight, Search, X as CloseIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../../store/slices/authSlice";
import { useTranslation } from "react-i18next";
import styles from "./Formulations.module.css";
import FormulationCard from "./FormulationCard";
import FormulationDetailModal from "./FormulationDetailModal";

const ALL = "All";

export default function 
Formulations({ limit = null }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");
  const isAuthenticated = !!localStorage.getItem("token");
  const navigate = useNavigate();

  const [selectedFormulation, setSelectedFormulation] = useState(null);
  const [formulations, setFormulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState(ALL);
  const [activeType, setActiveType] = useState(ALL);

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const handleAction = (callbackAction) => {
    if (!isAuthenticated) {
      navigate("/login");
    } else {
      callbackAction();
    }
  };

  const fetchData = async (pageNum = 1) => {
    try {
      const response = await api.get(`/recipes?page=${pageNum}&per_page=20`);
      if (response.data.code === 200) {
        setFormulations((prev) =>
          pageNum === 1 ? response.data.data : [...prev, ...response.data.data],
        );
        setPagination(response.data.pagination || null);
      }
    } catch (error) {
      console.error("Fetch Error:", error.response?.data || error.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setPage(1);
    fetchData(1);
  }, [i18n.language]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    setPage(nextPage);
    fetchData(nextPage);
  };

  const categoriesList = useMemo(() => {
    if (!formulations || formulations.length === 0) return [ALL];
    const allNames = formulations.flatMap(
      (f) => f.categories?.map((cat) => cat.name) || [],
    );
    return [ALL, ...new Set(allNames)];
  }, [formulations]);

  const filteredAll = useMemo(() => {
    const q = search.toLowerCase();
    return formulations.filter((f) => {
      const matchCat =
        activeCat === ALL ||
        f.categories?.some((cat) => cat.name === activeCat);

      const matchType = activeType === ALL || f.type?.name === activeType;

 const matchSearch =
   !q ||
   f.name.toLowerCase().includes(q) ||
   f.description?.toLowerCase().includes(q) ||
   f.ingredients?.some?.((ing) =>
     [ing.chemical_name, ing.trade_name]
       .filter(Boolean)
       .some((val) => val.toLowerCase().includes(q)),
   );

      return matchCat && matchType && matchSearch;
    });
  }, [formulations, search, activeCat, activeType]);

  const filtered = useMemo(
    () => (limit ? filteredAll.slice(0, limit) : filteredAll),
    [filteredAll, limit],
  );

  const hasMore = limit && filteredAll.length > limit;

  const canLoadMore =
    !limit && pagination && pagination.current_page < pagination.last_page;

  return (
    <section className={styles.section} dir={isRtl ? "rtl" : "ltr"}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <span className={styles.badge}>{t("formulations.labBadge")}</span>
            <h2 className={styles.title}>{t("formulations.title")}</h2>
            <p className={styles.subtitle}>{t("formulations.subtitle")}</p>
          </div>
          {hasMore && (
            <button
              className={styles.viewMoreLink}
              onClick={() => handleAction(() => navigate("/top-formula"))}
            >
              {t("common.viewMore")}
              <ArrowRight
                size={14}
                style={{ transform: isRtl ? "rotate(180deg)" : "none" }}
              />
            </button>
          )}
        </div>

        <div className={styles.searchBox}>
          <Search size={18} />
          <input
            type="text"
            placeholder={t("formulations.searchPlaceholder")}
            value={search}
            onFocus={() => !isAuthenticated && navigate("/login")}
            onChange={(e) => isAuthenticated && setSearch(e.target.value)}
          />
          {search && (
            <button
              className={styles.clearBtn}
              onClick={() => handleAction(() => setSearch(""))}
            >
              <CloseIcon size={14} />
            </button>
          )}
        </div>

        <div className={styles.filters}>
          {categoriesList.map((cat) => (
            <button
              key={cat}
              className={`${styles.filterBtn} ${activeCat === cat ? styles.active : ""}`}
              onClick={() => handleAction(() => setActiveCat(cat))}
            >
              {cat === ALL ? t("common.all") : cat}
              {cat !== ALL && (
                <span className={styles.filterCount}>
                  {
                    formulations.filter((f) =>
                      f.categories?.some((c) => c.name === cat),
                    ).length
                  }
                </span>
              )}
            </button>
          ))}
        </div>

        {!loading && (
          <p className={styles.resultsCount}>
            {filteredAll.length} {t("formulations.resultsFound")}
          </p>
        )}

        {loading ? (
          <div className={styles.loadingWrap}>
            {[...Array(limit || 8)].map((_, i) => (
              <div key={i} className={styles.skeleton} />
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className={styles.grid}>
            {filtered.map((formulation) => (
              <FormulationCard
                key={formulation.id}
                formulation={formulation}
                onClick={() =>
                  handleAction(() => setSelectedFormulation(formulation))
                }
              />
            ))}
          </div>
        ) : !isAuthenticated ? (
          <div className={styles.guestBox}>
            <div className={styles.guestIcon}>🧪</div>
            <h3>{t("formulations.guestTitle")}</h3>
            <p>{t("formulations.guestMsg")}</p>
            <span>{t("formulations.guestHint")}</span>
            <button
              className={styles.loginBtn}
              onClick={() => navigate("/login")}
            >
              {t("common.login")}
            </button>
          </div>
        ) : (
          <div className={styles.empty}>
            <Search size={48} strokeWidth={1.2} />
            <p>{t("formulations.noResults")}</p>
            <span>{t("formulations.tryAgain")}</span>
          </div>
        )}

        {canLoadMore && (
          <div className={styles.loadMoreWrap}>
            <button
              className={styles.loadMoreBtn}
              onClick={() => handleAction(handleLoadMore)}
              disabled={loadingMore}
            >
              {loadingMore ? "..." : t("common.loadMore")}
            </button>
          </div>
        )}
      </div>

      {selectedFormulation && (
        <FormulationDetailModal
          formulation={selectedFormulation}
          onClose={() => setSelectedFormulation(null)}
        />
      )}
    </section>
  );
}
